import type { CompanySummary, Paginated } from '@obras/shared';
import { formatCnpj, formatPhone } from '@obras/shared';
import { useQuery } from '@tanstack/react-query';
import { Building2, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

import { AppShell, PageHeader } from '../components/AppShell.js';
import { CompanyFormModal } from '../features/empresas/CompanyFormModal.js';
import { useDeleteCompany } from '../features/empresas/api.js';
import { Badge } from '../components/ui/Badge.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { SearchInput } from '../components/ui/Field.js';
import { EmptyState, ErrorState, FormError, Skeleton } from '../components/ui/Feedback.js';
import { Modal } from '../components/ui/Modal.js';
import { api, ApiError, queryString } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

const ICON = { strokeWidth: 1.5 } as const;

/** Espera a digitação parar antes de consultar — igual às demais listagens. */
function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debounced;
}

/**
 * Confirmação de exclusão.
 *
 * O servidor recusa enquanto houver obra vinculada; a tela antecipa a recusa
 * quando já sabe a contagem, para não oferecer um botão que volta com "não".
 * O caminho para essa empresa é marcá-la como inativa, no editar.
 */
function DeleteCompanyModal({
  empresa,
  onClose,
}: {
  empresa: CompanySummary | null;
  onClose: () => void;
}) {
  const remover = useDeleteCompany();
  const [erro, setErro] = useState<string | null>(null);

  const nome = empresa?.tradeName ?? empresa?.legalName ?? '';
  const vinculada = (empresa?.obrasCount ?? 0) > 0;

  function fechar(): void {
    setErro(null);
    onClose();
  }

  async function confirmar(): Promise<void> {
    if (!empresa) return;
    setErro(null);

    try {
      await remover.mutateAsync(empresa.id);
      fechar();
    } catch (error) {
      setErro(error instanceof ApiError ? error.message : 'Não foi possível remover a empresa.');
    }
  }

  return (
    <Modal
      open={empresa !== null}
      onClose={fechar}
      title="Remover empresa"
      description="A empresa sai das listagens; o histórico e a auditoria são preservados."
      footer={
        <>
          <Button variant="ghost" onClick={fechar} disabled={remover.isPending}>
            {vinculada ? 'Fechar' : 'Cancelar'}
          </Button>
          {vinculada ? null : (
            <Button
              variant="danger"
              loading={remover.isPending}
              icon={<Trash2 {...ICON} size={16} />}
              onClick={() => void confirmar()}
            >
              Remover empresa
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <FormError>{erro}</FormError>

        {vinculada ? (
          <p className="text-muted-foreground">
            <strong className="font-semibold text-foreground">{nome}</strong> está vinculada a{' '}
            {empresa?.obrasCount === 1 ? '1 obra' : `${empresa?.obrasCount} obras`} e por isso não
            pode ser removida. Para tirá-la da lista de escolha sem apagar o histórico, marque-a
            como inativa no cadastro.
          </p>
        ) : (
          <p className="text-muted-foreground">
            Confirma a remoção de <strong className="font-semibold text-foreground">{nome}</strong>?
            A exclusão é lógica, e o CNPJ volta a ficar disponível para um novo cadastro.
          </p>
        )}
      </div>
    </Modal>
  );
}

export function EmpresasPage() {
  const { may } = useAuth();
  const [formOpen, setFormOpen] = useState(false);
  const [editando, setEditando] = useState<CompanySummary | null>(null);
  const [excluindo, setExcluindo] = useState<CompanySummary | null>(null);
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebounced(search);

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['empresas', 'list', debouncedSearch],
    queryFn: ({ signal }) =>
      api.get<Paginated<CompanySummary>>(
        `/empresas${queryString({ perPage: 100, search: debouncedSearch || undefined })}`,
        signal,
      ),
    placeholderData: (anterior) => anterior,
  });

  /* Quem cadastra obra precisa cadastrar a contratada (ADR 041); é caixinha, ligada por padrão para fiscal e gestor. */
  const podeEditar = may('canManageCompanies');
  /* Excluir é outra caixinha (ADR 052): o que some da lista some para todo mundo. */
  const podeExcluir = may('canDeleteCompanies');

  return (
    <AppShell>
      <PageHeader
        title="Empresas"
        description="Empresas contratadas, com o total de obras vinculadas."
        action={
          podeEditar ? (
            <Button
              variant="primary"
              icon={<Plus {...ICON} size={16} />}
              onClick={() => setFormOpen(true)}
            >
              Nova empresa
            </Button>
          ) : null
        }
      />

      <div className="mb-4 flex justify-end">
        <div className="relative w-full sm:w-80">
          <Search
            {...ICON}
            size={16}
            className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <SearchInput
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por nome ou CNPJ"
            aria-label="Buscar empresas"
            className="pr-3 pl-9"
          />
        </div>
      </div>

      {isError ? (
        <ErrorState
          message={error instanceof Error ? error.message : 'Erro desconhecido.'}
          onRetry={() => void refetch()}
        />
      ) : isPending ? (
        <Card className="p-5">
          <div className="flex flex-col gap-3">
            {Array.from({ length: 5 }, (_, index) => (
              <Skeleton key={index} className="h-12" />
            ))}
          </div>
        </Card>
      ) : data.items.length === 0 ? (
        <Card>
          <EmptyState
            icon={Building2}
            title="Nenhuma empresa cadastrada"
            description="Cadastre as contratadas para vincular às obras e acompanhar o faturamento por empresa."
            action={
              podeEditar ? (
                <Button
                  variant="primary"
                  icon={<Plus {...ICON} size={16} />}
                  onClick={() => setFormOpen(true)}
                >
                  Nova empresa
                </Button>
              ) : null
            }
          />
        </Card>
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-left">
            <caption className="sr-only">Empresas contratadas</caption>
            <thead className="bg-muted">
              <tr className="text-[11px] font-medium tracking-[0.04em] text-muted-foreground uppercase">
                <th scope="col" className="px-5 py-3">
                  Razão social
                </th>
                <th scope="col" className="px-5 py-3">
                  CNPJ
                </th>
                <th scope="col" className="px-5 py-3">
                  Contato
                </th>
                <th scope="col" className="px-5 py-3 text-right">
                  Obras
                </th>
                <th scope="col" className="px-5 py-3">
                  Situação
                </th>
                <th scope="col" className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {data.items.map((company) => (
                <tr
                  key={company.id}
                  /*
                    Sem realce ao passar o mouse: a linha não é clicável, e o
                    destaque prometia uma ação que não existe. É o mesmo
                    critério da tabela de usuários.
                  */
                  className="border-t border-border"
                >
                  <td className="px-5 py-3">
                    <span className="block font-medium">
                      {company.tradeName ?? company.legalName}
                    </span>
                    {company.tradeName ? (
                      <span className="block text-[13px] text-muted-foreground">
                        {company.legalName}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-5 py-3 font-mono text-[13px]">
                    {company.cnpj ? formatCnpj(company.cnpj) : '—'}
                  </td>
                  <td className="px-5 py-3 text-[13px] text-muted-foreground">
                    {company.email ?? (company.phone ? formatPhone(company.phone) : '—')}
                  </td>
                  <td className="tabular px-5 py-3 text-right font-medium">{company.obrasCount}</td>
                  <td className="px-5 py-3">
                    <Badge tone={company.status === 'ativa' ? 'success' : 'muted'}>
                      {company.status === 'ativa' ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {podeEditar ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="size-8"
                          title={`Editar ${company.tradeName ?? company.legalName}`}
                          aria-label={`Editar ${company.tradeName ?? company.legalName}`}
                          onClick={() => setEditando(company)}
                        >
                          <Pencil {...ICON} size={16} aria-hidden />
                        </Button>
                      ) : null}
                      {podeExcluir ? (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="size-8 hover:bg-danger-bg hover:text-danger"
                          title={`Remover ${company.tradeName ?? company.legalName}`}
                          aria-label={`Remover ${company.tradeName ?? company.legalName}`}
                          onClick={() => setExcluindo(company)}
                        >
                          <Trash2 {...ICON} size={16} aria-hidden />
                        </Button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <CompanyFormModal
        open={formOpen || editando !== null}
        editando={editando}
        onClose={() => {
          setFormOpen(false);
          setEditando(null);
        }}
      />

      <DeleteCompanyModal empresa={excluindo} onClose={() => setExcluindo(null)} />
    </AppShell>
  );
}
